import { useEffect, useRef, useState, type ReactNode } from 'react'
import { gsap, useGSAP } from '@/lib/gsap'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { cn } from '@/lib/utils'

const muteText = (_dark: boolean) => 'text-lc-mute'
const inkText = (_dark: boolean) => 'text-lc-ink'

function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const on = () => setMobile(mq.matches)
    on()
    if (mq.addEventListener) mq.addEventListener('change', on)
    else mq.addListener(on) // Safari < 14 fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on)
      else mq.removeListener(on)
    }
  }, [])
  return mobile
}

/* A single pinned horizontal rail: the title stays put while the row of
   cards scrubs sideways with vertical scroll. Shared by the home events and
   daily-programs sections and the placeholder events rail. */
export function PinnedCardRail({ id, heading, cards }: { id?: string; heading: ReactNode; cards: ReactNode[] }) {
  const section = useRef<HTMLElement>(null)
  const track = useRef<HTMLDivElement>(null)
  const reduced = usePrefersReducedMotion()
  const mobile = useIsMobile()

  useGSAP(() => {
    // never pin on phones — and re-run (cleaning up any pin created on the
    // first render) the moment useIsMobile flips, or a stale pin-spacer would
    // leak and overlap the next section
    if (reduced || mobile || !track.current || !section.current) return
    const el = track.current
    const getDist = () => el.scrollWidth - window.innerWidth

    /* the card nearest the viewport centre is in focus: full size and
       opacity; its neighbours sit back a little */
    const setFocus = () => {
      const x = Number(gsap.getProperty(el, 'x'))
      const mid = window.innerWidth / 2
      const kids = Array.from(el.children) as HTMLElement[]
      let best = 0
      let bestDist = Infinity
      kids.forEach((k, i) => {
        const c = k.offsetLeft + x + k.offsetWidth / 2
        const d = Math.abs(c - mid)
        if (d < bestDist) { bestDist = d; best = i }
      })
      kids.forEach((k, i) => {
        const base = k.dataset.rot ?? '0'
        k.style.transform = `rotate(${base}deg) scale(${i === best ? 1.05 : 0.94})`
        k.style.opacity = i === best ? '1' : '0.55'
      })
    }

    const tween = gsap.to(el, {
      x: () => -getDist(),
      ease: 'none',
      scrollTrigger: {
        trigger: section.current,
        start: 'top top',
        end: () => `+=${getDist()}`,
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: setFocus,
        onRefresh: setFocus,
      },
    })
    setFocus()
    return () => { tween.scrollTrigger?.kill(); tween.kill() }
  }, { scope: section, dependencies: [reduced, mobile] })

  /* phones (and reduced motion): drop the pinned scroll-jack that was cutting
     the tall cards off — a plain horizontal swipe with snap points instead */
  if (reduced || mobile) {
    return (
      <section id={id} className="relative py-16">
        {heading}
        <div className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-6 [&>*]:snap-center">{cards}</div>
      </section>
    )
  }

  return (
    <section ref={section} id={id} className="relative">
      {/* the pinned viewport holds BOTH the title and the rail, so the title
          stays visible for the whole horizontal ride */}
      <div className="rail-pin flex h-screen flex-col justify-center overflow-hidden pt-28">
        {heading}
        <div
          ref={track}
          className="flex w-max items-stretch gap-7 py-3 will-change-transform pl-[calc(50vw-160px)] pr-[calc(50vw-160px)] md:pl-[calc(50vw-180px)] md:pr-[calc(50vw-180px)]"
        >
          {cards}
        </div>
      </div>
    </section>
  )
}

export function RailHeading({ dark, kicker, titleA, titleWord, titleB, intro }: { dark: boolean; kicker: string; titleA: string; titleWord: string; titleB: string; intro: string }) {
  return (
    <div className="rail-heading container-lc px-5 pb-8 text-center">
      <p className={cn('label', muteText(dark))}>{kicker}</p>
      <h2 className={cn('mx-auto mt-3 max-w-[22ch] font-display text-[clamp(1.8rem,1.2rem+2.6vw,3.2rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
        {titleA}{' '}
        <span className="inline-block -rotate-1 rounded-[0.28em] bg-lime px-[0.2em] pb-[0.05em] text-[#141D12]">{titleWord}</span>{titleB}
      </h2>
      <p className={cn('rail-intro mx-auto mt-3 max-w-lg text-lg', muteText(dark))}>{intro}</p>
    </div>
  )
}
